import * as React from "react";

import { cn } from "@/lib/utils";

type AnyProps = Record<string, unknown>;

function mergeRefs<T>(...refs: Array<React.Ref<T> | undefined>) {
  return (node: T | null) => {
    for (const ref of refs) {
      if (typeof ref === "function") ref(node);
      else if (ref && "current" in ref) (ref as React.RefObject<T | null>).current = node;
    }
  };
}

function mergeProps(slotProps: AnyProps, childProps: AnyProps): AnyProps {
  const merged: AnyProps = { ...childProps };

  for (const key in slotProps) {
    const slotValue = slotProps[key];
    const childValue = childProps[key];

    if (/^on[A-Z]/.test(key) && typeof slotValue === "function") {
      merged[key] =
        typeof childValue === "function"
          ? (...args: unknown[]) => {
              (childValue as (...a: unknown[]) => unknown)(...args);
              (slotValue as (...a: unknown[]) => unknown)(...args);
            }
          : slotValue;
    } else if (key === "className" && (typeof slotValue === "string" || slotValue == null)) {
      merged.className = cn(slotValue as string | undefined, childValue as string | undefined);
    } else if (key === "style") {
      merged.style = { ...(slotValue as object), ...(childValue as object) };
    } else {
      merged[key] = slotValue;
    }
  }

  return merged;
}

/**
 * Clones a single child element and merges the parent's props onto it.
 * Mirrors Radix `asChild` / Base UI `render` polymorphism.
 */
export const Slot = React.forwardRef<HTMLElement, React.HTMLAttributes<HTMLElement>>(function Slot(
  { children, ...slotProps },
  forwardedRef,
) {
  if (!React.isValidElement(children)) return null;

  const child = children as React.ReactElement<AnyProps & { ref?: React.Ref<unknown> }>;
  const merged = mergeProps(slotProps as AnyProps, child.props);
  merged.ref = mergeRefs(forwardedRef as React.Ref<unknown>, child.props.ref);

  return React.cloneElement(child, merged);
});
